// core/utils/storageHelper.ts

/**
 * Helper to manage storage paths and resized image variants
 * generated by the storage-resize-images extension.
 */
export const storageHelper = {
  /**
   * Returns the URL for a resized version of an image.
   * Based on the configuration in extensions/image-resizer.env
   * 
   * @param originalUrl The original image URL from Firebase Storage
   * @param size The size suffix (e.g., '200x200', '800x800', '1200x1200')
   * @returns The URL for the resized version or the original if not applicable
   */
  getResizedImageUrl(originalUrl: string | undefined, size: '200x200' | '800x800' | '1200x1200'): string {
    if (!originalUrl) return '';
    if (!originalUrl.includes('firebasestorage.googleapis.com')) return originalUrl;
    
    // The extension is configured to save resized images in the 'resized' folder
    // Original path example: .../b/app-pescar.firebasestorage.app/o/posts%2Fimage.jpg?alt=media...
    // Resized path example: .../b/app-pescar.firebasestorage.app/o/resized%2Fposts%2Fimage_200x200.jpg?alt=media...
    
    try {
      // 1. Extract path after /o/
      const urlParts = originalUrl.split('/o/');
      if (urlParts.length < 2) return originalUrl;
      
      const beforeO = urlParts[0];
      const afterO = urlParts[1].split('?')[0]; // Path before query params
      const queryParams = urlParts[1].split('?')[1] || '';
      
      // 2. Decode the path (it's URI encoded, e.g., posts%2Fimage.jpg)
      const decodedPath = decodeURIComponent(afterO);
      
      // 3. Construct the resized path
      // extension usually adds the suffix before the extension
      const lastDotIndex = decodedPath.lastIndexOf('.');
      const pathWithoutExt = lastDotIndex !== -1 ? decodedPath.substring(0, lastDotIndex) : decodedPath;
      const extension = lastDotIndex !== -1 ? decodedPath.substring(lastDotIndex) : '';
      
      const resizedPath = `resized/${pathWithoutExt}_${size}${extension}`;
      const encodedResizedPath = encodeURIComponent(resizedPath);
      
      return `${beforeO}/o/${encodedResizedPath}?${queryParams}`;
    } catch (error) {
      console.warn('[StorageHelper] Error parsing image URL for resizing:', error);
      return originalUrl;
    }
  },

  /**
   * Helper for avatar thumbnails
   */
  getAvatarThumbnail(url: string | undefined): string {
    return this.getResizedImageUrl(url, '200x200');
  },

  /**
   * Helper for post feed previews
   */
  getPostPreview(url: string | undefined): string {
    return this.getResizedImageUrl(url, '800x800');
  }
};
